import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@/components/ui/button";
import { useLogin, useVerifyLoginMfa } from "../../hooks/use-login";
import { LoginForm } from "./login-form";
import { VerifyMfaForm } from "../change-details/verify-mfa-form";

const schema = yup
    .object({
        email: yup
            .string()
            .email("Invalid email")
            .required("Email is required"),
        password: yup.string().required("Password is required"),
    })
    .required();

const codeSchema = yup
    .object({
        code: yup
            .string()
            .required("Please enter the code")
            .matches(/^\d{6}$/, "Enter the 6-digit code"),
    })
    .required();

export const LoginUser = () => {
    const location = useLocation();

    const [resolver, setResolver] = useState(null);

    const methods = useForm({
        resolver: yupResolver(schema),
    });

    const codeMethods = useForm({
        resolver: yupResolver(codeSchema),
    });

    const mutation = useLogin();
    const verifyMutation = useVerifyLoginMfa();

    const onSubmit = (data) => {
        mutation.mutate(data, {
            onSuccess: (result) => {
                if (result?.mfaRequired) {
                    setResolver(result.resolver);
                }
            },
        });
    };

    const onVerify = ({ code }) => {
        verifyMutation.mutate({
            resolver,
            code,
            locationState: location.state,
        });
    };

    if (resolver) {
        return (
            <div className="w-full">
                <div>
                    <h1 className="mb-3 text-3xl/6 font-bold">
                        Enter your verification code
                    </h1>

                    <p className="mb-3 text-sm">
                        Enter the 6-digit code from your authenticator app.
                    </p>
                </div>

                <VerifyMfaForm
                    methods={codeMethods}
                    onSubmit={onVerify}
                    isPending={verifyMutation.isPending}
                    className="*:block *:space-y-3 [&_button]:mt-3!"
                />

                <div className="mt-4 flex gap-3">
                    <Button
                        variant="link"
                        size="sm"
                        onClick={() => setResolver(null)}
                    >
                        Go back
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full">
            <div>
                <h1 className="mb-3 text-3xl/6 font-bold">
                    Sign in to your account
                </h1>

                <p className="mb-3 text-sm">
                    Don't have an account?{" "}
                    <Link
                        to="/register"
                        state={location.state}
                        className="no-underline hover:underline"
                    >
                        <strong>Sign up</strong>
                    </Link>
                </p>
            </div>

            <LoginForm
                methods={methods}
                mutation={mutation}
                onSubmit={onSubmit}
                locationState={location.state}
            />
        </div>
    );
};
